import { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import {MeshBasicMaterial, Color} from "three";
import styled, { keyframes } from "styled-components";
import { breakpoints } from "@/layouts/breakpoints";

/* ─── Keyframes ─────────────────────────────────────────────────────────────── */
const fadeUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`
  0%   { background-position: -400px 0 }
  100% { background-position:  400px 0 }
`;

/* ─── Styled components ──────────────────────────────────────────────────────── */
const Shell = styled.section`
  position: relative;
  width: 100%;
  min-hegith: 800px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${breakpoints.small}) {
    max-height: 120vh;
    min-height: 800px;
  }
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height%3E%3C/svg%3E");
  background-size: 180px 180px;
  pointer-events: none;
  z-index: 1;
  opacity: 0.6;
`;

const Bloom = styled.div`
  position: absolute;
  bottom: -10%;
  left: 50%;
  transform: translateX(-50%);
  width: 860px;
  height: 520px;
  background: radial-gradient(ellipse at 50% 80%, rgba(36,90,200,0.18) 0%, rgba(10,40,120,0.10) 40%, transparent 70%);
  pointer-events: none;
  z-index: 1;
`;

const Hero = styled.div`
  position: relative;
  z-index: 10;
  width: calc(100% - 160px);
  max-width: 2000px;
  pointer-events: none;
  padding: 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: ${breakpoints.medium}) {
    padding: 80px 40px;
    width: calc(100% - 80px);
  }
`;

const Eyebrow = styled.p`
  font-size: 0.62rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.75s 0.05s cubic-bezier(0.16,1,0.3,1) both;
`;

const Heading = styled.h2`
  font-size: clamp(3rem, 5.5vw, 5rem);
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.75s 0.1s cubic-bezier(0.16,1,0.3,1) both;

  @media (max-width: ${breakpoints.small}) {
    font-size: 48px!important;
  }
  em {
    background: linear-gradient(90deg, #6aa3ff 0%, #a8c8ff 50%, #6aa3ff 100%);
    background-size: 400px 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
  }
`;

const Body = styled.p`
  font-size: 0.75rem;
  line-height: 1.9;
  max-width: 460px;
  margin-bottom: 48px;
  animation: ${fadeUp} 0.75s 0.15s cubic-bezier(0.16,1,0.3,1) both;
`;

const StatsStrip = styled.div`
  display: flex;
  gap: 0;
  animation: ${fadeUp} 0.75s 0.2s cubic-bezier(0.16,1,0.3,1) both;
  flex-wrap: wrap;
  width: 50%;
  @media (max-width: ${breakpoints.medium}) {
    width: 100%;
  }
`;

const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 16px 32px 16px 0;
  margin-right: 32px;
  border-right: 1px solid rgba(255,255,255,0.06);
  @media (max-width: ${breakpoints.medium}) {
    padding: 16px 0;
  }
  &:last-child {
    border-right: none;
    margin-right: 0;
  }
`;

const StatValue = styled.span`
  font-weight: 300;
  font-size: 2rem;
  line-height: 1;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.span`
  font-size: 0.58rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(120,150,210,0.4);
`;

const GlobeClip = styled.div`
  position: relative;
  z-index: 5;
  width: 100%;
  height: 600px;
  overflow: hidden;
  margin-top: -400px;
  padding-left: 55vw;
  display: flex;
  justify-content: center;
  mask-image: radial-gradient(ellipse 90% 100% at 50% 0%, black 60%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 90% 100% at 50% 0%, black 60%, transparent 100%);

  @media (max-width: ${breakpoints.large}) {
    padding-left: 100vw;
  }
  @media (max-width: ${breakpoints.medium}) {
    margin-top: -150px;
    padding-left: 0vw;
    pointer-events:none;
  }
  canvas{
    margin-top: -200px;
  }
`;

/* ─── Types ──────────────────────────────────────────────────────────────────── */
export type GlobeMarker = {
  id: string;
  location: [number, number]; /* [lat, lng] */
  label: string;
};

export type GlobeStat = { v: string; l: string };

export type GlobeHeroProps = {
  eyebrow?: string;
  headingPlain?: string;
  headingEm?: string;
  body?: string;
  stats?: GlobeStat[];
  markers?: GlobeMarker[];
};

/* ─── Defaults ───────────────────────────────────────────────────────────────── */
const DEFAULT_STATS: GlobeStat[] = [
  { v: "37+",    l: "Exchanges"   },
  { v: "35+",    l: "Years History" },
  { v: "20yr",   l: "History"     },
  { v: "99.98%", l: "Uptime SLA"  },
];

const DEFAULT_MARKERS: GlobeMarker[] = [
  { id: "nyse",     location: [40.71, -74.01],  label: "NYSE"      },
  { id: "nasdaq",   location: [40.76, -73.98],  label: "NASDAQ"    },
  { id: "tsx",      location: [43.65, -79.38],  label: "TSX"       },
  { id: "b3",       location: [-23.55, -46.63], label: "B3"        },
  { id: "bmv",      location: [19.43, -99.13],  label: "BMV"       },
  { id: "lse",      location: [51.51, -0.09],   label: "LSE"       },
  { id: "euronext", location: [48.87,  2.33],   label: "Euronext"  },
  { id: "xetra",    location: [50.11,  8.68],   label: "Xetra"     },
  { id: "moex",     location: [55.75, 37.62],   label: "MOEX"      },
  { id: "tadawul",  location: [24.69, 46.72],   label: "Tadawul"   },
  { id: "bse",      location: [18.93, 72.83],   label: "BSE"       },
  { id: "sse",      location: [31.23, 121.47],  label: "SSE"       },
  { id: "hkex",     location: [22.28, 114.16],  label: "HKEX"      },
  { id: "tse",      location: [35.68, 139.77],  label: "TSE"       },
  { id: "asx",      location: [-33.87, 151.21], label: "ASX"       },
  { id: "sgx",      location: [1.28, 103.85],   label: "SGX"       },
  { id: "jse",      location: [-26.20, 28.04],  label: "JSE"       },
];

/* ─── Marker HTML ────────────────────────────────────────────────────────────── */
function markerHtml(label: string): string {
  return `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="
        font-size:9px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;
        color:#fff;background:#0047FF;border-radius:3px;padding:3px 8px;
        white-space:nowrap;font-family:sans-serif;line-height:1.4;
      ">${label}</div>
      <div style="width:1px;height:8px;background:rgba(0,71,255,0.55);"></div>
      <div style="width:5px;height:5px;border-radius:50%;background:#0047FF;"></div>
    </div>
  `;
}

/* ─── Component ──────────────────────────────────────────────────────────────── */
export default function GlobeHero({
  eyebrow      = "",
  headingPlain = "",
  headingEm    = "",
  body         = "",
  stats        = DEFAULT_STATS,
  markers      = DEFAULT_MARKERS,
}: GlobeHeroProps) {
  const globeRef = useRef<any>(null);
  const [countries, setCountries] = useState<any[]>([]);

  /* ── Fetch GeoJSON - just the raw features, no processing at all ─────────── */
  useEffect(() => {
    fetch("/earthpoints.geojson")
      .then(r => r.json())
      .then(data => setCountries(data.features))
      .catch(err => console.warn("GlobeHero: failed to load earthpoints.geojson", err));
  }, []);

  /* ── Auto-spin + kill default lights ────────────────────────────────────── */
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;

    const controls = globe.controls();
    if (controls) {
      controls.autoRotate      = true;
      controls.autoRotateSpeed = 0.6;
      controls.enableZoom      = false;
      controls.enablePan       = false;
    }

    /*
     * react-globe.gl injects a DirectionalLight + AmbientLight by default.
     * MeshBasicMaterial ignores all lights, but removing them is clean hygiene
     * and ensures no future material change re-introduces the top-down shadow.
     */
    const scene = globe.scene();
    if (scene) {
      const toRemove = scene.children.filter(
        (c: any) => c.isLight
      );
      toRemove.forEach((light: any) => scene.remove(light));
    }
  }, []);

  /* ── Globe material: unlit white sphere ─────────────────────────────────── */
  const globeMaterial = useMemo(() => new MeshBasicMaterial({
    color: new Color("#f5f7fc"),
  }), []);

  /* ── Markers ─────────────────────────────────────────────────────────────── */
  const htmlMarkers = useMemo(
    () => markers.map(m => ({
      lat:  m.location[0],
      lng:  m.location[1],
      html: markerHtml(m.label),
    })),
    [markers]
  );

  return (
    <Shell>
      <Grain />
      <Bloom />

      <Hero>
        <Eyebrow className="sans">{eyebrow}</Eyebrow>
        <Heading className="serif">
          {headingPlain}{headingPlain && <br />}
          {headingEm && <em>{headingEm}</em>}
        </Heading>
        {body && <Body>{body}</Body>}

        {stats.length > 0 && (
          <StatsStrip>
            {stats.map(s => (
              <StatCell key={s.l}>
                <StatValue>{s.v}</StatValue>
                <StatLabel>{s.l}</StatLabel>
              </StatCell>
            ))}
          </StatsStrip>
        )}
      </Hero>

      <GlobeClip>
        <Globe
          ref={globeRef}
          backgroundColor="rgba(0,0,0,0)"
          showAtmosphere={false}
          showGlobe={true}
          globeMaterial={globeMaterial}
          /*
           * hexPolygonsData renders GeoJSON polygon features directly as GPU hex tiles.
           * Zero JS grid sampling, zero geoContains - the library handles it entirely
           * on the GPU side via three-globe's internal h3 hex binning.
           *
           * hexPolygonMargin(0.3) - gap between tiles; lower = denser/more filled
           * hexPolygonResolution(3) - h3 resolution; 3 = coarse dots like the reference
           * hexPolygonColor - dark dots on the white sphere
           */
          hexPolygonsData={countries}
          hexPolygonResolution={3}
          hexPolygonMargin={0.75}
          hexPolygonColor={() => "#0d1a2e"}
          hexPolygonAltitude={0.005}
          /* ── Markers ── */
          htmlElementsData={htmlMarkers}
          htmlLat="lat"
          htmlLng="lng"
          htmlElement={(d: any) => {
            const el = document.createElement("div");
            el.innerHTML = d.html;
            return el.firstElementChild as HTMLElement;
          }}
          width={1800}
          height={1800}
        />
      </GlobeClip>
    </Shell>
  );
}
